/**
 * Which triggers survive on a RESOLVED forecast's timeline.
 *
 * A settled question has no pending triggers: once a verdict lands, only events that
 * had already occurred by the resolution moment remain on the spine. This is the pure
 * half of that rule, extracted from ForecastLane's `shownMain` so it can be tested
 * directly (the component-side half is geometry — clamping survivors onto the marker).
 *
 * No React imports — this is `lib/`.
 */

/**
 * Has `e` occurred at or before `tMs`?
 *
 * Effective end is `date_end || date` — the same test `isPast` itself is stamped with
 * (`stampEventsIsPastAsOf` in App.jsx, `occurredBy` in reforecastEvents.js), so this
 * agrees with the stored flag wherever that flag is fresh.
 *
 * @param {object} e an event/trigger
 * @param {number} tMs boundary in epoch ms
 * @param {(v: string|null|undefined) => number} evMs date → epoch ms (NaN when unparseable)
 * @returns {boolean} false for an absent/unparseable date
 */
export const occurredBy = (e, tMs, evMs) => {
  // `date_end` WHEN PARSEABLE, else fall back to `date` — the canonical `endDateOf`
  // rule (reforecastEvents.js). `evMs(e.date_end || e.date)` is NOT the same thing: a
  // present-but-garbage `date_end` is truthy, so it short-circuits the `||` and yields
  // NaN, dropping an event whose `date` was perfectly valid. That is the very failure
  // this module exists to stop, so it must not reintroduce it one field over.
  // `date` is REQUIRED and is validated FIRST. Deciding on `date_end` alone would let an
  // event with a missing or malformed `date` survive whenever its end parsed — and that
  // survivor is stamped past, so `eventAnchorMs` averages `new Date(undefined)` into the
  // window midpoint and returns NaN before its finite guard, poisoning the resolved
  // lane's layout. It would also break this function's own contract (false for an
  // unparseable date). No start, no occurrence.
  const start = evMs(e?.date);
  if (isNaN(start)) return false;
  const end = evMs(e?.date_end);
  return (isNaN(end) ? start : end) <= tMs;
};

/**
 * Keep only the triggers that had occurred by `dropBoundaryMs`, stamped past.
 *
 * Occurrence is decided against the boundary rather than by trusting each event's
 * stored `isPast`. That flag is stamped when the forecast RUNS and is only refreshed by
 * a reforecast (`mergeReforecastEvents`), so a trigger that was pending at the last run
 * and then happened before the question settled still carries `isPast: false`. Trusting
 * it dropped triggers that demonstrably occurred — and a forecast that resolved before
 * its FIRST reforecast (any same-day question) lost its entire timeline.
 *
 * Survivors are stamped `isPast: true` because they occurred by construction, and
 * everything downstream renders off that flag (grey ✓ vs pending pie) — leaving a stale
 * false would draw an occurred trigger as a PENDING pie, the exact state this rule
 * exists to prevent.
 *
 * @param {object[]} events the lane's events
 * @param {number|null} dropBoundaryMs resolution moment in epoch ms, or null when unresolved
 * @param {(v: string|null|undefined) => number} evMs date → epoch ms (NaN when unparseable)
 * @returns {object[]} survivors (the input, unchanged, when `dropBoundaryMs` is null)
 */
export function filterResolvedEvents(events, dropBoundaryMs, evMs) {
  const list = Array.isArray(events) ? events : [];
  if (dropBoundaryMs == null) return list;
  return list
    .filter((e) => occurredBy(e, dropBoundaryMs, evMs))
    .map((e) => {
      // A survivor must be RENDERABLE, not merely kept. `occurredBy` now rescues an event
      // whose `date_end` is garbage by falling back to `date` — but leaving that garbage on
      // the object hands the renderer a trap: App.jsx's `eventAnchorMs` treats any truthy
      // `date_end !== date` as a window and averages the two, so an unparseable one makes
      // the midpoint NaN. Its `Number.isFinite` guard sits AFTER `if (ev.isPast) return raw`,
      // and these survivors are stamped past — so the NaN escapes unguarded and poisons
      // layout. Drop the unparseable field here instead: the anchor then falls to `date`,
      // which is the value `occurredBy` already judged the event on.
      const badEnd = e.date_end != null && e.date_end !== "" && isNaN(evMs(e.date_end));
      if (e.isPast && !badEnd) return e;
      const out = { ...e, isPast: true };
      if (badEnd) out.date_end = null;
      return out;
    });
}
